import type { ActiveAnalysis } from "../../../api/useActiveAnalysesStream";
import type { Setup } from "../../../api/useSetups";
import { color } from "@ui";
import { getAxisId } from "../Chart/hooks/useQuantityYAxes";
import type { SelectedQuantities } from "../useSelectedQuantities";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { NORMALIZED_GAP_Y_AXIS } from "./NormalizedGap/useOptions";
import getQuantityOffset from "../../../api/getQuantityOffset";
import getQuantitySigma from "../../../api/getQuantitySigma";
import type { MetrologyOverrides } from "../../../api/useMetrologyOverrides";
import { isAnalysisQuantityValid } from "../metrology";
export const SIGMA_AREA_SERIE_ID = "sigma-area";
const SIGMA_COUNT = 2;

function get2SigmaSerie({
  analyses,
  selectedQuantities,
  isGap,
  normalized = false,
  setups,
  metrologyOverrides: _metrologyOverrides,
  displayInvalidPoints,
}: {
  analyses: ActiveAnalysis[];
  selectedQuantities: SelectedQuantities;
  isGap: boolean;
  normalized: boolean;
  setups: Setup[];
  metrologyOverrides: MetrologyOverrides;
  displayInvalidPoints: boolean;
}): Highcharts.SeriesArearangeOptions {
  const points = analyses.reduce<Highcharts.PointOptionsObject[]>((acc, analysis, index) => {
    const previousAnalysis = analyses[index - 1];
    if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
      acc.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        high: undefined,
        low: undefined,
      });
    }

    for (const { quantity } of selectedQuantities) {
      const quantityValue = analysis.quantityValues[quantity.id];
      if (!quantityValue) return acc;

      if (!displayInvalidPoints) {
        const valid = isAnalysisQuantityValid(analysis, quantity);
        if (!valid) {
          acc.push({
            id: `analysis-id-${analysis.id}`,
            x: analysis.date,
            high: undefined,
            low: undefined,
          });
          continue;
        }
      }

      const analysisSetup = getAnalysisSetup(setups, analysis);
      const offset = getQuantityOffset(analysisSetup, quantity.id);
      const sigma = getQuantitySigma(analysisSetup, quantity.id);

      const expected = quantityValue.simulated === null ? null : quantityValue.simulated + offset;
      const twoSigma = sigma === null ? null : SIGMA_COUNT * sigma;
      const base = expected === null ? null : isGap ? 0 : expected;
      const high = normalized
        ? 2
        : base === null || twoSigma === null
          ? undefined
          : base + twoSigma;
      const low = normalized
        ? -2
        : base === null || twoSigma === null
          ? undefined
          : base - twoSigma;

      acc.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        high,
        low,
      });
    }

    return acc;
  }, []);

  return {
    id: SIGMA_AREA_SERIE_ID,
    data: points,
    type: "arearange" as const,
    replicateInNavigator: false,
    color: color.chlorine100,
    showInLegend: false,
    yAxis: normalized
      ? NORMALIZED_GAP_Y_AXIS
      : getAxisId(selectedQuantities[0].quantity, false, false),
  };
}

export default get2SigmaSerie;
